import React, { useState, useEffect, useRef } from "react";
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import AccordionNextButton from "../AccordionNextButton";
import TagContainer from "./TagContainer";
import InactiveTagContainer from "./InactiveTagContainer";
import HttpService from "../../../HttpService";
import "animate.css/animate.css";
import './styles/Tags.css';
import GlobalFixes from "./GlobalFixes";

const Tags = (props) => {
  const [tagList, setTagList] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAccordionActive, setIsAccordionActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // New state to track if the panel was previously open
  const wasAccordionActive = useRef(false);

  useEffect(() => {
    // Track when accordion becomes active
    const isNowActive = props.expanded === 3;
    setIsAccordionActive(isNowActive);

    // If the accordion was active and is now closing, save the tags
    if (wasAccordionActive.current && !isNowActive) {
      saveTags();
    }

    // Update the ref for the next render
    wasAccordionActive.current = isNowActive;

    // Only fetch if we're on the Tags panel (panel 3) AND we have the required data AND not already loading
    if (isNowActive &&
        !isLoading && // Prevent duplicate calls
        props.info[2].quoteDescriptions_data?.weight !== undefined &&
        props.info[2].quoteDescriptions_data?.pieces !== undefined) {
      fetchQuoteTags();
    }
  }, [props.expanded, props.info[2].quoteDescriptions_data?.weight, props.info[2].quoteDescriptions_data?.pieces, isAccordionActive]);


  const fetchQuoteTags = async () => {
    if (isLoading) {
      return; // Prevent duplicate calls
    }

    setIsLoading(true);

    try {
      const response = await HttpService.getQuoteTags(props.quoteId);
      var tempTags = [];

      await response.data.forEach((element) => {
        try {
          const restrictions = element.variableDefinition !== null ? JSON.parse(element.variableDefinition) : null;
          element.checked = element.alwaysOn || element.defaultOn;
          if(restrictions !== undefined && restrictions !== null) {
            let resList = [];
            restrictions.forEach((restriction) => {
              if (restriction.type === "dropdown" ) {
                if (Array.isArray(restriction.values)) {
                  restriction.choices = restriction.values.map((item) => {
                    return {label: item};
                  });
                } else if (typeof restriction.values === 'object' && restriction.values !== null) {
                  restriction.choices = Object.entries(restriction.values).map(([key, displayText]) => ({
                    label: displayText,
                    value: key
                  }));
                }
                restriction.value = restriction.default;
              }
              else {
                if(restriction.type === "checkbox") {
                  const d = typeof restriction.default === 'string' ? restriction.default.trim().toLowerCase() : restriction.default;
                  restriction.value = (d === 'true' || d === '1' || d === 1 || d === true) ? 1 : 0;
                }
                else if(restriction.default)
                  restriction.value = restriction.default;
                else
                  restriction.value = '';
              }
              restriction.prompt = (!restriction.prompt) ? element.clientLabel : restriction.prompt;

              // Initial validation check is done here (don't use addAlert)
              if(restriction.newline === undefined || restriction.newline === '0') {
                restriction.newline = false;
                resList.push(restriction);
              }
              else {
                resList.push({param: "newLine", type: "newline", newline: true});
                restriction.newline = false;
                resList.push(restriction)
              }
            })
            element.restrictions = resList;
          }
          else {
            element.restrictions = [{ type: 'none' }];
          }
          tempTags.push(element);
        }
        catch(e) {
          console.log(element.variableDefinition + "-exception-" + e);
        }
      });
      setTagList(tempTags);

      // Report selected tag labels to parent for accordion summary
      reportSelectedTags(tempTags);

      // Initial validation check
      validateRequiredFields(tempTags);
    } catch (error) {
      console.error("Error in fetchQuoteTags:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (id) => (event) => {
    props.onChange({"target": event.name, "value": event.value});
    const updatedTagList = tagList.map(element => {
      if(element.quoteTagID === id) {
        const updatedRestrictions = element.restrictions.map(restriction => {
          if(restriction.param === event.name) {
            return {...restriction, value: event.value};
          }
          return restriction;
        });
        return {...element, restrictions: updatedRestrictions};
      }
      return element;
    });
    setTagList(updatedTagList);
    // props.setCompletedStep(3); // TEMP: Commented out - this resets step 3 when blur fires on accordion close

    // Run validation after each change
    validateRequiredFields(updatedTagList);
  };

  // Report selected tag data to parent for accordion summary display
  const reportSelectedTags = (tags) => {
    const selectedTagsData = tags
      .filter(tag => tag.checked)
      .map(tag => ({
        label: tag.clientLabel || tag.label,
        icon_url: tag.icon_url
      }))
      .filter(tag => tag.label);
    props.onChange({"target": "selectedTagsData", "value": selectedTagsData});
  };

  const handlerCheckedChange = (id) => {
    const updatedTagList = tagList.map(tag => {
      if (tag.quoteTagID === id) {
        const isChecked = !tag.checked;
        tag.checked = isChecked;

        if (isChecked) {
          const element = document.querySelector(`#tag-${id}`);
          if (element) {
            element.classList.add("flash");
            setTimeout(() => element.classList.remove("flash"), 500);
          }
        }
      }
      return tag;
    });

    setTagList(updatedTagList);

    // Report selected tag labels to parent for accordion summary
    reportSelectedTags(updatedTagList);

    // Run validation after changing checked state
    validateRequiredFields(updatedTagList);
  };

  // REMOVED: Don't handle alerts here to prevent popups - all validation at button level
  const handleAlert = {
    addAlert: (event) => {
      // Don't add alerts to prevent popups - validation handled at Next button
    },
    removeAlert: (event) => {
      // Don't remove alerts - validation handled at Next button
    }
  };

  // Validate all required fields based on the 5 logic cases
  const validateRequiredFields = (tags) => {
    let missingFieldsCase4 = []; // Case 4: Always required
    let missingFieldsCase2 = []; // Case 2: Required if tag is active

    tags.forEach((element) => {
      if (element.checked && element.restrictions) {
        element.restrictions.forEach((restriction) => {
          // Skip structural separators only
          if (restriction.newline) {
            return;
          }

          const fieldRequired = restriction.required === "1" || restriction.required === 1 || restriction.required === true;
          const alwaysOn = element.alwaysOn === 1;
          const isOnlyAddWithVariable = element.isOnlyAddwithVariable;

          let shouldValidate = false;
          let isCase2 = false;

          if (alwaysOn) {
            // Cases 4 & 5: "Required at Entry"
            if (fieldRequired) {
              // Case 4: "Tag is required, Variable Value is required"
              shouldValidate = true;
              isCase2 = false;
            }
            // Case 5: "Tag is required, Variable Value is not required" - no validation needed
          } else {
            // Cases 1, 2, 3: "null / Not Required / Req @ Pick / Req @ POD / Req @ POD"
            if (isOnlyAddWithVariable === true) {
              // Case 1: "Tag is not required, but if variables empty do not add tag to job"
              // Validation happens at submission level, not here
              shouldValidate = false;
            } else if (isOnlyAddWithVariable === false || isOnlyAddWithVariable === null) {
              if (fieldRequired) {
                // Case 2: "Tag is not required, but if tag is selected then the Variable is required"
                shouldValidate = true;
                isCase2 = true;
              }
              // Case 3: "Tag is not required, neither is variable value required" - no validation
            }
          }

          if (shouldValidate && isFieldEmpty(restriction)) {
            const fieldLabel = restriction.prompt || restriction.param;
            const tagLabel = element.clientLabel || element.label;

            if (isCase2) {
              missingFieldsCase2.push(`${fieldLabel} (${tagLabel})`);
            } else {
              missingFieldsCase4.push(fieldLabel);
            }
          }
        });
      }
    });

    let errorMessages = [];

    // Add Case 4 errors (always required)
    if (missingFieldsCase4.length > 0) {
      if (missingFieldsCase4.length === 1) {
        errorMessages.push(`Please fill in the required field: ${missingFieldsCase4[0]}`);
      } else {
        errorMessages.push(`Please fill in the required fields: ${missingFieldsCase4.join(', ')}`);
      }
    }

    // Add Case 2 errors (required if tag active)
    if (missingFieldsCase2.length > 0) {
      if (missingFieldsCase2.length === 1) {
        errorMessages.push(`Please fill in the required field or deselect tag: ${missingFieldsCase2[0]}`);
      } else {
        errorMessages.push(`Please fill in the required fields or deselect tags: ${missingFieldsCase2.join(', ')}`);
      }
    }

    if (errorMessages.length > 0) {
      setErrorMessage(errorMessages.join('\n'));
      return false;
    } else {
      setErrorMessage('');
      return true;
    }
  };

  const isFieldEmpty = (restriction) => {
    const v = restriction.value;
    if (v === null || v === undefined) return true;
    if (typeof v === 'string' && v.trim() === '') return true;
    if (typeof v === 'number' && isNaN(v)) return true;
    // Dayjs / Date objects: invalid dates count as empty
    if (typeof v === 'object' && typeof v.isValid === 'function' && !v.isValid()) return true;
    // Checkbox: unchecked (0 / false) counts as empty for required validation
    if (restriction.type === 'checkbox') {
      return !v || v === 0 || v === '0' || v === false;
    }
    return false;
  };

  const saveTags = async () => {
    for (const element of tagList) {
      let postTag = {};

      if(element.checked) {
        postTag.checked = "1";

        element.restrictions.forEach((restriction) => {
          if(!restriction.newline) {
            postTag[restriction.param] = restriction.value;
          }
        });

        try {
          // Pass the postTag object directly to HttpService
          let res = await HttpService.postQuoteTags(element.quoteTagID, postTag, props.quoteId);
        } catch (error) {
          console.error("Error posting quote tag:", error);
        }
      }
      else {
        postTag.checked = "0";

        try {
          let res = await HttpService.postQuoteTags(element.quoteTagID, postTag, props.quoteId);
        } catch (error) {
          console.error("Error posting quote tag:", error);
        }
      }
    }
  };

  const handelNextPanel = async () => {
    // Final validation before proceeding
    if(validateRequiredFields(tagList)) {
      await saveTags();
      props.nextPanel();
    }
  };

  return (
    <div className="tags-container">
      <GlobalFixes />
      <FormGroup>
        {/* Show loading indicator if needed */}
        {isLoading && (
          <div className="loading-indicator">
            Loading tags...
          </div>
        )}

        {/* Active Tags Section */}
        <div className="active-tags-container" >
          {tagList.filter(tag => tag.checked).map((element, index) => (
            <TagContainer
              key={element.quoteTagID}
              id={`tag-${element.quoteTagID}`}
              label={element.clientLabel}
              variant="outlined"
              required={element.alwaysOn} // Only pass required if alwaysOn
              onAlert={handleAlert}
              onChange={handleChange(element.quoteTagID)}
              onCheckedChange={() => handlerCheckedChange(element.quoteTagID)}
              info={element}
              restrictions={element.restrictions}
              element={element}
              isFirstTag={index === 0} // Add this line - focus only on first tag
              isTagsAccordionActive={isAccordionActive} // Pass accordion state
            />
          ))}
        </div>

        {/* Inactive Tags Section */}
        <div className="inactive-tags-section">
          <Typography variant="h6" className="section-title">
            Available Tags
          </Typography>
          <div className="inactive-tags-container">
            {tagList.filter(tag => !tag.checked).map((element) => (
              <InactiveTagContainer
                key={element.quoteTagID}
                id={`tag-${element.quoteTagID}`}
                label={element.clientLabel}
                variant="outlined"
                required={element.alwaysOn} // Only pass required if alwaysOn
                onAlert={handleAlert}
                onChange={handleChange(element.quoteTagID)}
                onCheckedChange={() => handlerCheckedChange(element.quoteTagID)}
                info={element}
                restrictions={element.restrictions}
                element={element}
              />
            ))}
          </div>
        </div>

        <div className="div-padding" />
        <AccordionNextButton
          onClick={handelNextPanel}
          disabled={errorMessage !== '' || isLoading}
          panelIndex={3}
          activePanel={props.expanded}
          errorMessage={errorMessage}
        />
      </FormGroup>
    </div>
  );
};

export default Tags;